// medplum-intake.js — ONLINE-PRIMARY intake magic-link family, RLS-native.
//
// The anonymous patient-intake surface (/intake, /intake/step/:n, review) runs
// through the existing same-origin intake gateway. The gateway verifies and
// binds the magic-link token, then passes only an authorized Encounter id plus
// the clinical answer to an exactly-pinned Bot. The token never enters the Bot
// input archive Medplum writes before each execution.
//
// SCOPE OF THIS WAVE (honest — see build notes / concerns):
//   step    → Bot  (anon-intake-step,    pure QuestionnaireResponse upsert)
//   consent → Bot  (anon-intake-consent, durable FHIR Consent write)
//   submit / section-patch → STAY ON EXPRESS. Both invoke OpenAI at request
//     time (runIntakeExtraction / patchCardWithCorrection). Bot-ifying them
//     would put an OpenAI key behind an un-rate-limited anonymous magic-link
//     token — the exact widened-anon-surface the orchestrator gate decision
//     refused for `extract`. Left on Express; flagged for the owner.
//   gaps    → STAYS ON EXPRESS. Rule-based (no LLM) but a 600-line agent-harness
//     port with ZERO security gain (the QR it reads is already token-gated the
//     same way) and the client treats any gaps failure as a graceful no-op.
//   extract / live-extract / photos → EXPRESS (gate/card decision).
//
// Bot failures MUST NOT silently drop clinical data. Failed step/consent writes
// land in a small localStorage retry buffer (encounter-scoped, survives reload),
// retry with backoff (≤5 attempts) and on window 'online', and surface honest
// UI state via `opd:intake-sync` — the mic/dictation path stays unblocked.
import React from 'react';
import { getPublicAppConfig } from './medplum-client.js';
import { apiUrl } from './net-base.js';
import { buildEncounterView } from './shape/encounter-view.js';

export const INTAKE_SYNC_EVENT = 'opd:intake-sync';
// Same localStorage key bridge.js uses for the header EN/हिं pill — the anon
// boot loaders below default meta.lang from it (ScreenBoot passes no lang).
const LANG_KEY = 'healther.opd.lang';
const BUFFER_PREFIX = 'healther.opd.intake.retry.';
const MAX_ATTEMPTS = 5;
const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000];

// ── Bot execute (defined before drain helpers — tryExecuteBufferedEntry calls this) ──

async function postIntakeJson(path, input) {
  const response = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input || {}),
  });
  const out = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(out.reason || out.error || `intake request failed (${response.status})`);
    error.status = response.status;
    if (response.status === 401 || response.status === 403) error.unauthorized = true;
    throw error;
  }
  return out;
}

async function executeIntakeBot(clientName, input) {
  if (clientName === 'intakeStep') {
    return postIntakeJson(
      `/api/v1/opd/encounters/${encodeURIComponent(input.encounterId)}/intake/step/${encodeURIComponent(input.step)}`,
      input,
    );
  }
  if (clientName === 'intakeConsent') {
    const out = await postIntakeJson(
      `/api/v1/opd/encounters/${encodeURIComponent(input.encounterId)}/consent/record`,
      input,
    );
    const consent = out.consent || {};
    return {
      consentId: consent.medplumConsentId || consent.id || null,
      status: consent.status,
      recording: consent.recording,
      whatsapp: consent.whatsapp,
      granted: out.consentStatus?.ok === true || consent.granted === true,
    };
  }
  throw new Error(`unknown intake gateway operation: ${clientName}`);
}

// ── ANON READ-PATH BOOT LOADERS (main-2, Wave-3A) ────────────────────────────
// Replace the Express GET /view/bootstrap.js READ leg for the anonymous
// magic-link surfaces (/intake, /intake/step/:n, /intake/review, /saathi,
// /tablet-intake/:id, /saathi/:id). The flow mirrors the staff consult loader
// (medplum-encounter.js) but for a POWERLESS caller: an execute-only anon
// ClientApplication $executes the chart-read Bot, which verifies the magic-link
// {token} FAIL-CLOSED inside itself and returns the RAW FHIR graph; the browser
// then runs the SAME buildEncounterView() shaper the consult uses (the DTO never
// diverges — AGENTS.md §7). Assembles the exact window.__OPD_DATA__ shape
// installBridge() consumes ({ meta, encounter|intake, hospital, currentUser:null,
// features }). Fail-loud: an unauthorized/expired token throws so ScreenBoot
// renders the designed error state instead of a silently half-blank page.

function readLang(lang) {
  if (lang) return lang;
  try { return localStorage.getItem(LANG_KEY) || 'en'; } catch (_) { return 'en'; }
}

function anonMeta(route, { encounterId, token, lang }) {
  return {
    route,
    encounterId: encounterId || null,
    token: token || null,
    lang: lang || 'en',
    now: new Date().toISOString(),
  };
}

// Executes the chart-read Bot as the kioskChartRead anon client and returns
// { r, cfg } — r is the Bot's { authorized, encounterId, graph, consentStatus }.
// Throws a tagged error (err.unauthorized / err.reason) on a refused token so
// callers can map it to the right ScreenBoot state; cfg carries hospital branding.
export async function runChartReadBot({ token, encounterId } = {}) {
  const input = { token: token || undefined, encounterId: encounterId || undefined };
  let r;
  try {
    r = await postIntakeJson('/api/v1/opd/anon/chart-read', input);
  } catch (error) {
    if (error?.unauthorized) {
      error.reason = error.message || 'unauthorized';
      throw error;
    }
    throw error;
  }
  if (!r || r.authorized === false) {
    const reason = (r && r.reason) || 'unauthorized';
    const err = new Error('chart-read unauthorized: ' + reason);
    err.reason = reason;
    err.unauthorized = true;
    throw err;
  }
  const cfg = await getPublicAppConfig();
  return { r, cfg };
}

// Boot payload for the intake magic-link surfaces (route 'intake'):
// /intake, /intake/step/:n, /intake/review, /saathi, /intake/photos.
export async function loadIntakeBoot({ id = '', token = '', lang = '' } = {}) {
  const { r, cfg } = await runChartReadBot({ token: token || undefined, encounterId: id || undefined });
  const encounter = buildEncounterView(r.graph);
  const encId = r.encounterId || (encounter.encounter && encounter.encounter.id) || id || null;
  const language = readLang(lang);
  return {
    meta: anonMeta('intake', { encounterId: encId, token, lang: language }),
    encounter,
    intake: { encounterId: encId, token, lang: language },
    hospital: (cfg && cfg.hospital) || {},
    currentUser: null,
    features: { rppg: false },
  };
}

// Boot payload for the anonymous ENCOUNTER surfaces reached by magic link
// (route 'encounter' on /tablet-intake/:id and /saathi/:id). Same graph→view
// shaping as intake; no embedded queue (that is staff/kiosk-device only).
export async function loadAnonEncounterBoot({ id = '', token = '', lang = '' } = {}) {
  const { r, cfg } = await runChartReadBot({ token: token || undefined, encounterId: id || undefined });
  const encounter = buildEncounterView(r.graph);
  const encId = r.encounterId || (encounter.encounter && encounter.encounter.id) || id || null;
  return {
    meta: anonMeta('encounter', { encounterId: encId, token, lang: readLang(lang) }),
    encounter,
    hospital: (cfg && cfg.hospital) || {},
    currentUser: null,
    features: { rppg: false },
  };
}

// IntakeReview's optimistic refresh: replaces the GET /view/encounter/:id fetch
// with the same chart-read Bot path. Returns { ok, intake, encounter } — the
// caller reads `intake` (falling back to `encounter.intake`), exactly what the
// Express endpoint's { ok, ...encounterView } response carried.
export async function refreshIntakeViaBot({ encounterId, token } = {}) {
  const { r } = await runChartReadBot({ token: token || undefined, encounterId: encounterId || undefined });
  const encounter = buildEncounterView(r.graph);
  return { ok: true, intake: encounter.intake, encounter };
}

// ── Retry buffer (localStorage, intake-anon only) ────────────────────────────

function ls() {
  try { return window.localStorage; } catch (_) { return null; }
}

function entryKey(encounterId, kind, step) {
  if (!encounterId) return '';
  if (kind === 'step') return `${BUFFER_PREFIX}${encounterId}.step${step}`;
  return `${BUFFER_PREFIX}${encounterId}.consent`;
}

function readEntry(key) {
  const store = ls();
  if (!store || !key) return null;
  try {
    const raw = store.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function writeEntry(key, entry) {
  const store = ls();
  if (!store || !key) return;
  try { store.setItem(key, JSON.stringify(entry)); } catch (_) {}
}

function removeEntryKey(key) {
  const store = ls();
  if (!store || !key) return;
  try { store.removeItem(key); } catch (_) {}
}

function listBufferedForEncounter(encounterId) {
  const store = ls();
  if (!store || !encounterId) return [];
  const prefix = `${BUFFER_PREFIX}${encounterId}.`;
  const out = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (!k || !k.startsWith(prefix)) continue;
    const e = readEntry(k);
    if (e) out.push({ key: k, ...e });
  }
  return out;
}

function emitIntakeSync(encounterId) {
  try {
    window.dispatchEvent(new CustomEvent(INTAKE_SYNC_EVENT, { detail: { encounterId: encounterId || null } }));
  } catch (_) {}
}

/** Exported for tests — pending vs exhausted counts for one encounter. */
export function getIntakeSyncSummary(encounterId) {
  const entries = listBufferedForEncounter(encounterId);
  let pending = 0;
  let failed = 0;
  for (const e of entries) {
    if (e.syncState === 'failed' || e.attempts >= MAX_ATTEMPTS) failed += 1;
    else pending += 1;
  }
  const phase = failed > 0 ? 'failed' : (pending > 0 ? 'pending' : 'synced');
  return { pending, failed, total: pending + failed, phase };
}

/** Maps counts → visual phase (mirrors sync-status.jsx). */
export function deriveIntakeSyncPhase({ pending = 0, failed = 0 } = {}) {
  if (failed > 0) return 'failed';
  if (pending > 0) return 'pending';
  return 'synced';
}

function coalesceStepText(existing, payload) {
  const chunk = String(payload.text || '').trim();
  if (!chunk) return existing?.fullText || '';
  if (payload.replace) return chunk;
  const prev = (existing && existing.fullText) || '';
  return prev ? `${prev} ${chunk}`.trim() : chunk;
}

function enqueueBuffered({ kind, clientName, encounterId, step, payload }) {
  const key = entryKey(encounterId, kind, step);
  if (!key) return;
  const existing = readEntry(key);
  const fullText = kind === 'step'
    ? coalesceStepText(existing, payload)
    : undefined;
  const entry = {
    kind,
    clientName,
    encounterId,
    step: step ?? null,
    payload: kind === 'step'
      ? { ...payload, text: fullText, replace: true }
      : { ...payload },
    fullText,
    attempts: existing?.attempts || 0,
    nextRetryAt: Date.now(),
    syncState: 'pending',
    createdAt: existing?.createdAt || Date.now(),
  };
  writeEntry(key, entry);
  emitIntakeSync(encounterId);
  scheduleDrain();
}

function markEntryFailed(key, entry) {
  writeEntry(key, { ...entry, syncState: 'failed', attempts: MAX_ATTEMPTS });
  emitIntakeSync(entry.encounterId);
}

function bumpAttempt(key, entry, err) {
  const cur = readEntry(key) || entry;
  const attempts = (cur.attempts || 0) + 1;
  if (attempts >= MAX_ATTEMPTS) {
    markEntryFailed(key, { ...cur, attempts, lastError: String(err?.message || err || 'unknown') });
    return;
  }
  const delay = BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)];
  writeEntry(key, {
    ...cur,
    attempts,
    nextRetryAt: Date.now() + delay,
    syncState: 'pending',
    lastError: String(err?.message || err || 'unknown'),
  });
  emitIntakeSync(cur.encounterId);
  scheduleDrain(delay);
}

let drainTimer = null;
let draining = false;
let drainQueued = false;

function scheduleDrain(delayMs = 500, force = false) {
  if (drainTimer) clearTimeout(drainTimer);
  drainTimer = setTimeout(() => {
    drainTimer = null;
    drainIntakeBuffer({ force });
  }, delayMs);
}

async function tryExecuteBufferedEntry(key, entry) {
  const out = await executeIntakeBot(entry.clientName, entry.payload);
  if (!out || out.authorized === false) {
    const reason = (out && out.reason) || 'rejected';
    throw new Error(`intake bot rejected: ${reason}`);
  }
  return out;
}

function resetRetrySchedule() {
  const store = ls();
  if (!store) return;
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (!key || !key.startsWith(BUFFER_PREFIX)) continue;
    const entry = readEntry(key);
    if (!entry || entry.syncState === 'failed' || entry.attempts >= MAX_ATTEMPTS) continue;
    writeEntry(key, { ...entry, nextRetryAt: Date.now() });
  }
}

async function flushPendingIntakeBuffer({ force = false } = {}) {
  // Treat missing onLine as online — Node/harness may expose navigator without it.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const store = ls();
  if (!store) return;
  if (force) resetRetrySchedule();
  const now = Date.now();
  const keys = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k && k.startsWith(BUFFER_PREFIX)) keys.push(k);
  }
  for (const key of keys) {
    const entry = readEntry(key);
    if (!entry || entry.syncState === 'failed' || entry.attempts >= MAX_ATTEMPTS) continue;
    if (!force && entry.nextRetryAt && entry.nextRetryAt > now) continue;
    try {
      await tryExecuteBufferedEntry(key, entry);
      removeEntryKey(key);
      emitIntakeSync(entry.encounterId);
    } catch (err) {
      bumpAttempt(key, readEntry(key) || entry, err);
    }
  }
}

/** Test hook — force a drain pass (verify scripts only). */
export async function __drainIntakeBufferForTests() {
  return drainIntakeBuffer({ force: true });
}

async function drainIntakeBuffer({ force = false } = {}) {
  if (draining) {
    if (!force) { drainQueued = true; return; }
    for (let i = 0; i < 50 && draining; i++) {
      await new Promise((r) => setTimeout(r, 20));
    }
    if (draining) { drainQueued = true; return; }
  }
  draining = true;
  try {
    await flushPendingIntakeBuffer({ force });
  } finally {
    draining = false;
    if (drainQueued) {
      drainQueued = false;
      scheduleDrain(0, force);
      return;
    }
    const store = ls();
    if (!store) return;
    const still = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(BUFFER_PREFIX)) {
        const e = readEntry(k);
        if (e && e.syncState !== 'failed' && e.attempts < MAX_ATTEMPTS) still.push(e);
      }
    }
    if (still.length) {
      const next = Math.min(...still.map((e) => e.nextRetryAt || 0));
      scheduleDrain(Math.max(250, next - Date.now()), false);
    }
  }
}

if (typeof window !== 'undefined' && !globalThis.__HEALTHER_DISABLE_INTAKE_DRAIN_BOOT__) {
  window.addEventListener('online', () => scheduleDrain(100, true));
  scheduleDrain(1500);
}

function stepSuccessBody(out) {
  return { ok: true, intake: { id: out.intakeId }, step: out.step, text: out.text, state: out.state, syncState: 'synced' };
}

function consentSuccessBody(out) {
  return {
    ok: true,
    consent: { id: out.consentId, status: out.status, recording: out.recording, whatsapp: out.whatsapp },
    consentStatus: { ok: out.granted, recording: out.recording },
    syncState: 'synced',
  };
}

function pendingBody(kind, encounterId, step) {
  const summary = getIntakeSyncSummary(encounterId);
  return {
    ok: false,
    pending: true,
    syncState: summary.phase === 'failed' ? 'failed' : 'pending',
    kind,
    step: step ?? undefined,
    ...summary,
  };
}

// POST /intake/step/:n  →  anon-intake-step Bot.
// Returns success body, or { ok:false, pending:true, syncState } when buffered
// for retry — NEVER silent null.
export async function intakeStepViaBot({ encounterId, token, step, text, lang, durationMs, replace }) {
  if (!encounterId || !token || !text) {
    return { ok: false, syncState: 'synced', reason: 'missing-input' };
  }
  const payload = {
    token, encounterId, step, text,
    lang: lang || 'en', durationMs: durationMs || 0, replace: !!replace,
  };
  try {
    const out = await executeIntakeBot('intakeStep', payload);
    if (!out || out.authorized === false) {
      enqueueBuffered({
        kind: 'step', clientName: 'intakeStep', encounterId, step, payload,
      });
      return pendingBody('step', encounterId, step);
    }
    removeEntryKey(entryKey(encounterId, 'step', step));
    emitIntakeSync(encounterId);
    return stepSuccessBody(out);
  } catch (e) {
    enqueueBuffered({
      kind: 'step', clientName: 'intakeStep', encounterId, step, payload,
    });
    return pendingBody('step', encounterId, step);
  }
}

// POST /consent/record  →  anon-intake-consent Bot (durable FHIR Consent).
export async function intakeConsentViaBot({ encounterId, token, recording, whatsapp, language, grantedBy, notes }) {
  const payload = {
    token, encounterId,
    recording: !!recording, whatsapp: !!whatsapp,
    language: language || 'mixed', grantedBy: grantedBy || 'patient', notes: notes || null,
  };
  try {
    const out = await executeIntakeBot('intakeConsent', payload);
    if (!out || out.authorized === false) {
      const reason = (out && out.reason) || 'consent-write-failed';
      enqueueBuffered({
        kind: 'consent', clientName: 'intakeConsent', encounterId, payload,
      });
      const summary = getIntakeSyncSummary(encounterId);
      if (summary.phase === 'failed') {
        throw new Error('intake consent rejected: ' + reason);
      }
      return { ok: false, pending: true, syncState: 'pending', reason, ...summary };
    }
    removeEntryKey(entryKey(encounterId, 'consent'));
    emitIntakeSync(encounterId);
    return consentSuccessBody(out);
  } catch (e) {
    if (e && /intake consent rejected/.test(String(e.message))) throw e;
    enqueueBuffered({
      kind: 'consent', clientName: 'intakeConsent', encounterId, payload,
    });
    const summary = getIntakeSyncSummary(encounterId);
    if (summary.phase === 'failed') {
      throw new Error('intake consent could not be saved — show this screen to the desk');
    }
    return { ok: false, pending: true, syncState: 'pending', reason: String(e.message || e), ...summary };
  }
}

// ── React UI: compact persistent sync indicator ─────────────────────────────

const SYNC_T = {
  warnBg: 'var(--opd-warn-bg, var(--warning-bg, #FFF4E0))',
  warn: 'var(--opd-warn, var(--warning-600, #C9810F))',
  dangerBg: 'var(--opd-danger-bg, #FDECEA)',
  danger: 'var(--opd-danger, #C8342B)',
  font: '"Inter","Noto Sans Devanagari",-apple-system,system-ui,sans-serif',
};

function syncBannerCopy(summary, lang) {
  const n = summary.total;
  const hi = lang === 'hi';
  if (summary.phase === 'failed') {
    return hi
      ? 'उत्तर सहेजे नहीं जा सके — यह स्क्रीन डेस्क को दिखाएँ'
      : 'Answers could not be saved — show this screen to the desk';
  }
  const unit = n === 1
    ? (hi ? 'उत्तर अभी सहेजा जा रहा है' : 'answer still saving')
    : (hi ? 'उत्तर अभी सहेजे जा रहे हैं' : 'answers still saving');
  const tail = hi ? ' — यह पेज बंद न करें' : ' — don\'t close this page';
  return `${n} ${unit}${tail}`;
}

export function useIntakeSyncStatus(encounterId) {
  const [summary, setSummary] = React.useState(() => getIntakeSyncSummary(encounterId));

  React.useEffect(() => {
    if (!encounterId) {
      setSummary({ pending: 0, failed: 0, total: 0, phase: 'synced' });
      return undefined;
    }
    const refresh = () => setSummary(getIntakeSyncSummary(encounterId));
    refresh();
    const onSync = (e) => {
      if (!e.detail || !e.detail.encounterId || e.detail.encounterId === encounterId) refresh();
    };
    window.addEventListener(INTAKE_SYNC_EVENT, onSync);
    window.addEventListener('online', refresh);
    return () => {
      window.removeEventListener(INTAKE_SYNC_EVENT, onSync);
      window.removeEventListener('online', refresh);
    };
  }, [encounterId]);

  return summary;
}

/** Amber while retrying; red after retries exhaust. Hidden when synced. */
export function IntakeSyncBanner({ encounterId, lang = 'en', style }) {
  const summary = useIntakeSyncStatus(encounterId);
  if (!encounterId || summary.phase === 'synced') return null;
  const failed = summary.phase === 'failed';
  return React.createElement('div', {
    role: 'status',
    'aria-live': 'polite',
    style: {
      flex: 'none',
      margin: '0 16px 10px',
      padding: '10px 14px',
      borderRadius: 10,
      border: `1px solid ${failed ? SYNC_T.danger : SYNC_T.warn}`,
      background: failed ? SYNC_T.dangerBg : SYNC_T.warnBg,
      color: failed ? SYNC_T.danger : SYNC_T.warn,
      font: `600 12.5px/1.45 ${SYNC_T.font}`,
      ...style,
    },
  }, syncBannerCopy(summary, lang));
}

/** Test hook — clear all buffered entries for an encounter. */
export function __clearIntakeBufferForEncounter(encounterId) {
  for (const e of listBufferedForEncounter(encounterId)) removeEntryKey(e.key);
  emitIntakeSync(encounterId);
}

/** Test hook — inspect buffer contents (verify scripts only). */
export function __intakeBufferDump() {
  const store = ls();
  if (!store) return [];
  const out = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (!key || !key.startsWith(BUFFER_PREFIX)) continue;
    out.push({ key, entry: readEntry(key) });
  }
  return out;
}
